import React, { useEffect, useState } from 'react'
import { Checkbox, Typography } from '@mui/material'
import { createStyles, withStyles } from '@mui/styles'
import { api } from '../../../infrastructure/api'
import { ExcelGeneratorContainer } from '../../../infrastructure/excelExport'
import { Claims } from '../../../infrastructure/signIn/models'
import { hasClaim } from '../../../infrastructure/signIn/userContext'
import { muiOptions, MuiProps } from '../../../infrastructure/materialUiThemeProvider'
import { t } from '../../../infrastructure/i18nextHelper'
import { ColumnDescriptor, MultipleSelect, Select, TableItem, TextField } from '../../common/customComponents'
import { MasterDataItem, MasterDataShell, createExcelLines } from './masterDataShell'
import guid from '../../../infrastructure/guid'

type Vehicle = {
    id?: string | null
    vehicleId: string
    vehicleName: string
    meanOfTransportation: string
    isActive: boolean
    countries: string[]
}

type VehicleFilters = {
    vehicleId: string
    vehicleName: string
    countries: string
    meanOfTransportation: string
    isActive: string
}

const emptyFilters: VehicleFilters = {
    vehicleId: '',
    vehicleName: '',
    countries: '',
    meanOfTransportation: '',
    isActive: ''
}

const emptyVehicle = (): Vehicle => ({
    vehicleId: '',
    vehicleName: '',
    meanOfTransportation: '',
    isActive: true,
    countries: []
})

const toTableItem = (vehicle: Vehicle): TableItem<MasterDataItem<Vehicle>> => ({
    ...vehicle,
    id: vehicle.vehicleId,
    isModified: false
})

function VehicleMasterData({ classes }: MuiProps) {
    const tBase = 'admin.masterdata.vehicle.'
    const excelGenerator = ExcelGeneratorContainer.useContainer()
    const isManager = hasClaim(Claims.MasterdataVehicleManager)

    const [vehicles, setVehicles] = useState<Vehicle[]>([])
    const [countries, setCountries] = useState<string[]>([])
    const [meanOfTransportations, setMeanOfTransportations] = useState<string[]>([])
    const [filters, setFilters] = useState<VehicleFilters>(emptyFilters)
    const [selectedVehicles, setSelectedVehicles] = useState<TableItem<MasterDataItem<Vehicle>>[]>([])

    const load = async () => {
        const [vehicles, countries, modes] = await Promise.all([
            api.get<Vehicle[]>('masterdata/vehicle'),
            api.get<string[]>('masterdata/vehicle/country'),
            api.get<string[]>('masterdata/vehicle/mot')
        ])
        setVehicles(vehicles)
        setCountries(countries)
        setMeanOfTransportations(modes)
    }

    useEffect(() => { load() }, [])

    const getItems = () => vehicles.filter(vehicle =>
        vehicle.vehicleId.toLowerCase().includes(filters.vehicleId.toLowerCase())
        && vehicle.vehicleName.toLowerCase().includes(filters.vehicleName.toLowerCase())
        && vehicle.countries.join(', ').toLowerCase().includes(filters.countries.toLowerCase())
        && vehicle.meanOfTransportation.toLowerCase().includes(filters.meanOfTransportation.toLowerCase())
        && (!filters.isActive || (vehicle.isActive ? t(tBase + 'yes') : t(tBase + 'no'))
            .toLowerCase().includes(filters.isActive.toLowerCase()))
    ).map(toTableItem)

    const columns: ColumnDescriptor<TableItem<MasterDataItem<Vehicle>>>[] = [
        {
            name: t(tBase + 'vehicleId'),
            value: x => x.vehicleId,
            columnFilter: {
                value: filters.vehicleId,
                onChange: vehicleId => setFilters({ ...filters, vehicleId: vehicleId ?? '' })
            }
        },
        {
            name: t(tBase + 'vehicleName'),
            value: x => x.vehicleName,
            columnFilter: {
                value: filters.vehicleName,
                onChange: vehicleName => setFilters({ ...filters, vehicleName: vehicleName ?? '' })
            }
        },
        {
            name: t(tBase + 'country'),
            value: x => x.countries.join(', '),
            columnFilter: {
                value: filters.countries,
                onChange: countries => setFilters({ ...filters, countries: countries ?? '' })
            }
        },
        {
            name: t(tBase + 'meanOfTransportation'),
            value: x => x.meanOfTransportation,
            columnFilter: {
                value: filters.meanOfTransportation,
                onChange: meanOfTransportation =>
                    setFilters({ ...filters, meanOfTransportation: meanOfTransportation ?? '' })
            }
        },
        {
            name: t(tBase + 'isActive'),
            value: x => x.isActive ? t(tBase + 'yes') : t(tBase + 'no'),
            columnFilter: {
                value: filters.isActive,
                onChange: isActive => setFilters({ ...filters, isActive: isActive ?? '' })
            }
        }
    ]

    const onSave = async (item: Vehicle) => {
        const id = item.id === guid.empty ? null : item.id
        await api.post('masterdata/vehicle', { ...item, id })
        await load()
        return true
    }

    const onImportExcel = (file: Blob) => {
        api.upload('masterdata/vehicle/import', file, 'vehicle', { withReport: 'dialog' }).then(load)
    }

    const exportExcel = () => {
        const items = selectedVehicles.length > 0 ? selectedVehicles : getItems()
        excelGenerator.generate({
            filename: 'Vehicles.xlsx',
            sheets: [{ name: 'Vehicles', lines: createExcelLines(items, columns) }]
        })
    }

    return (
        <MasterDataShell
            tableId='vehicle-table'
            headerLabel={t(tBase + 'vehicles')}
            itemLabel={t(tBase + 'vehicle')}
            items={getItems()}
            isManager={isManager}
            onExportExcel={exportExcel}
            onImportExcel={onImportExcel}
            onNew={emptyVehicle}
            onSave={isManager ? onSave : undefined}
            columns={columns}
            onSelectionChange={items =>
                setSelectedVehicles(items as TableItem<MasterDataItem<Vehicle>>[])}>
            {(selectedItem, setSelectedItem) => (
                <>
                    <TextField label={t(tBase + 'vehicleId')}
                        disabled={!isManager || selectedItem.id !== guid.empty}
                        text={selectedItem.vehicleId}
                        onChange={event => setSelectedItem({ ...selectedItem, vehicleId: event.target.value })} />
                    <TextField label={t(tBase + 'vehicleName')}
                        disabled={!isManager}
                        text={selectedItem.vehicleName}
                        onChange={event => setSelectedItem({ ...selectedItem, vehicleName: event.target.value })} />
                    <MultipleSelect label={t(tBase + 'country')}
                        chips={true}
                        allWhenEmpty={false}
                        disabled={!isManager}
                        values={selectedItem.countries}
                        choices={countries.map(value => ({ value, text: value }))}
                        onChange={countries => setSelectedItem({ ...selectedItem, countries })} />
                    <Select label={t(tBase + 'meanOfTransportation')}
                        disabled={!isManager}
                        value={selectedItem.meanOfTransportation}
                        choices={meanOfTransportations.map(value => ({ value, text: value }))}
                        onChange={meanOfTransportation => {
                            if (meanOfTransportation)
                                setSelectedItem({ ...selectedItem, meanOfTransportation })
                        }} />
                    <div className={classes.isActiveContainer}>
                        <Typography variant='subtitle1'>{t(tBase + 'isActive')}</Typography>
                        <Checkbox checked={selectedItem.isActive}
                            disabled={!isManager}
                            onChange={event => setSelectedItem({ ...selectedItem, isActive: event.target.checked })} />
                    </div>
                </>
            )}
        </MasterDataShell>
    )
}

const styles = () => createStyles({ isActiveContainer: { display: 'flex', alignItems: 'center' } })

export default withStyles(styles, muiOptions)(VehicleMasterData)